import React, { useCallback, useEffect, useState } from 'react';
import {
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import api from '../../services/api';
import type { UserEconomyRow } from '../../types/accountUserEconomy';
import { formatAccountMetricValue } from '../../utils/accountMetricFormat';
import { formatContributorsSectionTitle } from '../../utils/accountDashboardFocus';
import { useTaskDashboard } from './TaskDashboardContext';

interface TaskContributorsTableProps {
  accountId: string;
  economyVisible: boolean;
}

interface FallbackUserRow {
  userId: string;
  displayName: string | null;
  email: string;
  role: string;
}

export default function TaskContributorsTable({
  accountId,
  economyVisible
}: TaskContributorsTableProps) {
  const { taskId, metric, granularity, anchor, focusWindow, currencyCode } = useTaskDashboard();

  const [rows, setRows] = useState<UserEconomyRow[] | FallbackUserRow[]>([]);
  const [loading, setLoading] = useState(false);

  const sectionTitle = formatContributorsSectionTitle(focusWindow?.label, granularity);

  const loadContributors = useCallback(async () => {
    try {
      setLoading(true);
      if (economyVisible) {
        const res = await api.getTaskUserEconomy(accountId, taskId, { metric, granularity, anchor });
        if (res.success) {
          setRows(res.data.users ?? []);
        } else {
          setRows([]);
        }
      } else {
        const res = await api.getAccountUsers(accountId);
        const users = (res.data ?? []).map(
          (u: { id: string; displayName?: string; email: string; role: string }) => ({
            userId: u.id,
            displayName: u.displayName ?? null,
            email: u.email,
            role: u.role
          })
        );
        setRows(users);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, taskId, economyVisible, metric, granularity, anchor]);

  useEffect(() => {
    loadContributors();
  }, [loadContributors]);

  const formatRegistered = (value: number) => formatAccountMetricValue(value, metric, currencyCode);

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {sectionTitle}
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No users assigned to this account.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                {economyVisible && <TableCell align="right">Registered</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell>
                    <Typography variant="body2">{row.displayName || row.email}</Typography>
                    {row.displayName && (
                      <Typography variant="caption" color="text.secondary">
                        {row.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={row.role} size="small" />
                  </TableCell>
                  {economyVisible && (
                    <TableCell align="right">
                      {formatRegistered((row as UserEconomyRow).registered ?? 0)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
